import { useState } from 'react';
import { useForm, FormProvider, type Resolver } from "react-hook-form";
import { zodResolver } from '@hookform/resolvers/zod';

import SmallSpinner from "@/components/shared/spinners/small-spinner";
import ChangeTitulaire from "@/pages/profile/change-rib/change-titulaire";
import Files from "@/components/forms/files";
import Swal from "sweetalert2";
import IbanField from "@/components/forms/iban-field";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CreditCard, Save, AlertTriangle, Upload } from 'lucide-react';
import { useChangeIban, type ChangeIbanForm } from '@/hooks/api/use-amendments';
import { useContracts } from '@/hooks/api/use-contracts';
import { useFeaturesDisabled } from '@/hooks/api/use-settings';
import { useCurrentUser } from '@/hooks/api/use-user';
import { getFirstContractInProgress } from '@/lib/contracts';
import { createChangeRibSchema, type ChangeRibValues } from '@/schemas/change-rib';
import type { Contract } from '@/types/api/contract';

/** Le schéma dépend du changement de titulaire, transmis en contexte du résolveur (comme avant). */
const changeRibResolver: Resolver<ChangeRibValues, boolean> = (values, context, options) =>
    (zodResolver(createChangeRibSchema(!!context)) as unknown as Resolver<ChangeRibValues, boolean>)(values, context, options);

const ChangeRib = () => {
    const contractsQuery = useContracts();
    const featuresDisabled = useFeaturesDisabled();

    if (contractsQuery.isPending) return <SmallSpinner />;

    return <ChangeRibForm featuresDisabled={featuresDisabled} />;
};

const ChangeRibForm = ({ featuresDisabled }: { featuresDisabled: boolean }) => {
    const user = useCurrentUser();
    const contracts = useContracts().data?.data ?? [];
    const contrat: Partial<Contract> = contracts.length > 0 ? (getFirstContractInProgress(contracts) || contracts[0]) : {};
    const postAvenantChangeIban = useChangeIban();
    const loading = postAvenantChangeIban.isPending;
    const iban = user.banque?.iban;
    const [changeTitulaire, setChangeTitulaire] = useState(false);
    const souscripteur = contrat ? contrat.souscripteur?.nom : null;
    const navigate = useNavigate();

    const methods = useForm<ChangeRibValues, boolean>({
        defaultValues: {
            tiers: user.belair_id,
            adresse: '',
            cp: '',
            ville: '',
            titulaire: '',
            type: '',
            firstname: '',
            lastname: '',
            birthname: '',
            birthdate: '',
            birthcity: '',
            birthcountry: '',
            relationship: '',
            third_payeur_reason: '',
            files: {}
        },
        resolver: changeRibResolver,
        context: changeTitulaire,
        mode: 'onSubmit',
    });

    const { handleSubmit, formState: { errors }, watch, setValue } = methods;
    const values = watch();

    const handleFormSubmit = (values: ChangeRibValues) => {
        if (changeTitulaire && values?.relationship === 'A' && values.files['G'] === undefined) {
            void Swal.fire({
                title: 'Attention',
                text: 'Merci de nous transmettre votre RIB.',
                showConfirmButton: true,
                confirmButtonText: 'Terminer',
                confirmButtonColor: getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim(),
                showCloseButton: true
            });
        } else {
            postAvenantChangeIban.mutate({ contractId: values.contract || contrat.id || '', form: values as unknown as ChangeIbanForm, defaultTitulaire: souscripteur, changementTitulaire: changeTitulaire, successFunction: () => { void navigate('/mon-profil'); } });
        }
    };

    return (
        <div className="space-y-6">
            <div className="space-y-2 sm:space-y-3 mt-4">
                <h1 className="text-xl sm:text-2xl font-bold">Je modifie mes coordonnées bancaires</h1>
            </div>

            {featuresDisabled && (
                <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        <strong>Fonctionnalité indisponible pour le moment :</strong> prélèvement en cours
                    </AlertDescription>
                </Alert>
            )}

            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <Card>
                        <CardHeader className="pb-6 border-b">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <CreditCard className="w-5 h-5 text-primary" />
                                Coordonnées bancaires
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-5">
                            <div className="p-6 bg-muted/50 rounded-lg">
                                <p className="text-sm text-muted-foreground mb-0.5">Mon IBAN actuel</p>
                                <p className="font-semibold">{iban || 'Non renseigné'}</p>
                            </div>

                            <p className="font-semibold text-base">Mes nouvelles coordonnées</p>

                            <ChangeTitulaire
                                souscripteur={souscripteur}
                                contrat={contrat}
                                changeTitulaire={changeTitulaire}
                                setChangeTitulaire={setChangeTitulaire}
                                setValue={setValue}
                                values={values}
                                errors={errors}
                                handleChange={(e) => {
                                    const { name, value } = e.target;
                                    setValue(name as keyof ChangeRibValues, value, { shouldValidate: true });
                                }}
                                handleBlur={(e) => {
                                    const { name } = e.target;
                                    void methods.trigger(name as keyof ChangeRibValues);
                                }}
                            />

                            <IbanField />

                            {changeTitulaire && contrat.id && values.titulaire && (souscripteur ?? '').trim().toUpperCase() !== values.titulaire.trim().toUpperCase() && (
                                <div className="space-y-4">
                                    <Alert>
                                        <Upload className="h-4 w-4" />
                                        <AlertDescription>
                                            Merci de nous transmettre votre nouveau RIB.
                                        </AlertDescription>
                                    </Alert>
                                    <Files
                                        label={'RIB'}
                                        name={`files[G]`}
                                        arrayName={'files'}
                                        value={values.files}
                                        setValue={setValue}
                                        code={'G'}
                                    />
                                </div>
                            )}

                            <Alert variant="warning">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription>
                                    <p className="font-semibold mb-1">Attention :</p>
                                    <ul className="space-y-1 text-sm">
                                        <li className="flex items-start gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-warning mt-1.5 shrink-0" />
                                            Une mensualité / annuité peut encore être prélevée sur vos anciennes coordonnées bancaires.
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-warning mt-1.5 shrink-0" />
                                            La modification de l'IBAN concerne tous vos contrats.
                                        </li>
                                    </ul>
                                </AlertDescription>
                            </Alert>

                            <div className="pt-2">
                                {loading ? (
                                    <SmallSpinner />
                                ) : (
                                    <Button type="submit" className="w-full text-white cursor-pointer whitespace-normal py-6" disabled={featuresDisabled}>
                                        <Save className="w-4 h-4 shrink-0" />
                                        Je valide mes nouvelles coordonnées bancaires
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </form>
            </FormProvider>
        </div>
    );
};

export default ChangeRib;
